import { Box } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import SystemDialog, { type DialogCheckboxDescriptor } from "../../../../../components/UIToolkit/Popups/Dialog";
import { FONT_SIZE, INK } from "../../../../../theme/tokens";
import { agencySourceLabel } from "../../../../../utils/agencyIntegrations";
import type { AgencySource } from "../../../../../utils/models";

/** Stable identity, so the descriptor keeps working if the label copy is ever reworded. */
const GAP_CHECKBOX_ID = "gap-ack";

type Props = {
	open: boolean;
	source: AgencySource;
	onClose: () => void;
	onConfirm: () => void;
	confirmLoading?: boolean;
};

/**
 * "Turning it back on does not backfill the gap."
 *
 * Reactivation always confirms, even though it looks like the harmless direction. It is not: every
 * shift created or cancelled while the integration was off was never exchanged with the agency, and
 * switching back on resumes traffic from NOW rather than replaying anything. Saying so, and
 * requiring an acknowledgement of it, is the whole reason this dialog exists rather than a plain
 * flip of the switch.
 *
 * It no longer NAMES the window. The `hris` audit columns that carried "off at X" are gone — that
 * history is the Activity Log's now — and a dialog cannot read a log. The consequence is unchanged
 * and is the part the operator has to consent to, so the sentence states the consequence and leaves
 * the dates to the log.
 */
export const AgencyReactivationDialog: FC<Props> = ({ open, source, onClose, onConfirm, confirmLoading }) => {
	const { t } = useTranslation();
	const agency = agencySourceLabel(source);

	const checkboxes = useMemo<DialogCheckboxDescriptor[]>(
		() => [
			{
				id: GAP_CHECKBOX_ID,
				label: t("department.config.integrations.agency.reactivate.ack.label", { agency }),
				helperText: t("department.config.integrations.agency.reactivate.ack.helper", { agency }),
				required: true,
				defaultValue: false,
			},
		],
		[agency, t],
	);

	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t("department.config.integrations.agency.reactivate.title", { agency })}
			confirm={t("department.config.integrations.agency.reactivate.confirm", { agency })}
			confirmButtonVariant="primary"
			confirmLoading={confirmLoading}
			onConfirm={onConfirm}
			checkboxes={checkboxes}
			maxWidth="sm"
			fullWidth
			dataTestId="agency-reactivation-dialog"
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<Box component="p" sx={{ m: 0, fontSize: FONT_SIZE.SECONDARY, lineHeight: 1.5, color: INK.MUTED }}>
						{t("department.config.integrations.agency.reactivate.lead", { agency })}
					</Box>
					<InlineHint tone="warning" dataTestId="agency-reactivation-gap">
						{t("department.config.integrations.agency.reactivate.gap", { agency })}
					</InlineHint>
				</Box>
			}
		/>
	);
};
